import React, { useEffect, useState, useCallback } from "react";
import { Formik, Form, ErrorMessage } from "formik";
import * as Yup from "yup";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import errorToast from "../../components/toasts/errorToast";
import successToast from "../../components/toasts/successToast";
import { Spinner } from "react-bootstrap";
import { IoArrowBack } from "react-icons/io5";
import { profileView } from "../../redux/actions/profile";
import { getMySessionsApi, terminateSessionApi, terminateOtherSessionsApi } from "../../redux/apis/sessionApi";
import moment from "moment";

const ProfileScreen = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [loading, setLoading] = useState(false);
    const [profileData, setProfileData] = useState({});
    const [sessions, setSessions] = useState([]);
    const [loadingSessions, setLoadingSessions] = useState(false);
    const [terminatingId, setTerminatingId] = useState(null);
    const [terminatingOthers, setTerminatingOthers] = useState(false);

    const onViewSuccess = (data) => {
        setProfileData(data.data.data);
        setLoading(false);
    };

    const onViewError = (data) => {
        errorToast(data.data.message);
        setLoading(false);
    };

    useEffect(() => {
        setLoading(true);
        dispatch(profileView({}, onViewSuccess, onViewError));
    }, []);

    const fetchSessions = useCallback(async () => {
        setLoadingSessions(true);
        try {
            const res = await getMySessionsApi();
            if (res?.data?.data) setSessions(res.data.data);
        } catch (_) {} finally {
            setLoadingSessions(false);
        }
    }, []);

    useEffect(() => { fetchSessions(); }, [fetchSessions]);

    const handleTerminate = async (sessionId) => {
        setTerminatingId(sessionId);
        try {
            const res = await terminateSessionApi(sessionId);
            if (res?.data?.statusCode === 200) {
                successToast(res.data.message || 'Session terminated!');
                fetchSessions();
            } else {
                errorToast(res?.data?.message || 'Failed');
            }
        } catch (_) {
            errorToast('Failed to terminate session');
        } finally {
            setTerminatingId(null);
        }
    };

    const handleTerminateOthers = async () => {
        setTerminatingOthers(true);
        try {
            const res = await terminateOtherSessionsApi();
            if (res?.data?.statusCode === 200) {
                successToast(res.data.message || 'All other sessions terminated!');
                fetchSessions();
            } else {
                errorToast(res?.data?.message || 'Failed');
            }
        } catch (_) {
            errorToast('Failed to terminate sessions');
        } finally {
            setTerminatingOthers(false);
        }
    };

    const otherSessionsCount = sessions.filter(s => !s.is_current).length;

    const initialValues = {
        id: profileData?.id || "",
        salutation: profileData?.salutation || "",
        first_name: profileData?.first_name || "--",
        middle_name: profileData?.middle_name || "--",
        last_name: profileData?.last_name || "--",
        email: profileData?.email || "",
        sign_in: profileData?.sign_in || "",
        user_type: profileData?.user_type || "",
        company_name: profileData?.company?.company_name || "--",
    };

    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

    const validationSchema = Yup.object().shape({
        salutation: Yup.string().required("Required"),
        first_name: Yup.string().required("Required"),
        last_name: Yup.string().required("Required"),
        email: Yup.string().matches(emailRegex, "Invalid Email Format").required("Required"),
        sign_in: Yup.string().required("Required"),
        user_type: Yup.string().required("Required"),
        company_name: Yup.string().required("Required"),
    });

    return (
        <div className="d-flex vh-100 overflow-auto">
            <div className="w-100 ">
                <div className="w-100 d-flex align-items-center header border-bottom position-sticky top-0 bg-white py-2 z-1">
                    {/* <span className="px-1 fs_16 cursor_pointer pb-1" onClick={() => navigate(-1)}><IoArrowBack /></span> */}
                    <h2 className="fs_20 fw_400 fn_Roboto text-start px-2 pt-1">
                        Profile
                    </h2>
                </div>
                {loading ?
                    <div className="d-flex flex-column justify-content-center align-items-center" style={{ height: "90%" }}>
                        <Spinner animation="border" variant="primary" /> <span>Loading...</span>
                    </div>
                    :
                    <div className="flex-grow-1 pt-3">
                        <Formik
                            initialValues={initialValues}
                            validationSchema={validationSchema}
                            enableReinitialize
                        >
                            {({ values, setFieldValue, isValid, dirty }) => (
                                <Form className="d-flex flex-column h-100 justify-content-between">
                                    <div>
                                        <div className="text-start gap-3 mb-2 text-nowrap mx-4">
                                            <label htmlFor="salutation" className="py-1">
                                                Salutation
                                            </label>
                                            <div className="d-flex position-relative">
                                                <input
                                                    value={values.salutation}
                                                    className="input-field"
                                                    style={{ width: "20rem" }}
                                                >
                                                </input>
                                            </div>
                                            <ErrorMessage
                                                name="salutation"
                                                component="div"
                                                className="text-danger mt-1 fs_12"
                                            />
                                        </div>
                                        <div className="text-start gap-3 mb-2 text-nowrap mx-4">
                                            <label htmlFor="first_name" className="py-1">
                                                First Name
                                            </label>
                                            <div className="d-flex position-relative">
                                                <input
                                                    value={values.first_name}
                                                    className="input-field"
                                                    style={{ width: "20rem" }}
                                                />
                                            </div>
                                            <ErrorMessage
                                                name="first_name"
                                                component="div"
                                                className="text-danger mt-1 fs_12"
                                            />
                                        </div>
                                        <div className="text-start gap-3 mb-2 text-nowrap mx-4">
                                            <label htmlFor="middle_name" className="py-1">
                                                Middle Name
                                            </label>
                                            <div className="d-flex position-relative">
                                                <input
                                                    value={values.middle_name}
                                                    className="input-field"
                                                    style={{ width: "20rem" }}
                                                />
                                            </div>
                                            <ErrorMessage
                                                name="middle_name"
                                                component="div"
                                                className="text-danger mt-1 fs_12"
                                            />
                                        </div>
                                        <div className="text-start gap-3 mb-2 text-nowrap mx-4">
                                            <label htmlFor="last_name" className="py-1">
                                                Last Name
                                            </label>
                                            <div className="d-flex position-relative">
                                                <input
                                                    value={values.last_name}
                                                    className="input-field"
                                                    style={{ width: "20rem" }}
                                                />
                                            </div>
                                            <ErrorMessage
                                                name="last_name"
                                                component="div"
                                                className="text-danger mt-1 fs_12"
                                            />
                                        </div>
                                        <div className="text-start gap-3 mb-2 text-nowrap mx-4">
                                            <label htmlFor="company_name" className="py-1">
                                                Company Name
                                            </label>
                                            <div className="d-flex position-relative">
                                                <input
                                                    value={values.company_name}
                                                    className="input-field"
                                                    style={{ width: "20rem" }}
                                                />
                                            </div>
                                            <ErrorMessage
                                                name="company_name"
                                                component="div"
                                                className="text-danger mt-1 fs_12"
                                            />
                                        </div>
                                        <div className="text-start gap-3 mb-2 text-nowrap mx-4">
                                            <label htmlFor="email" className="py-1">
                                                Email
                                            </label>
                                            <div className="d-flex position-relative">
                                                <input
                                                    value={values.email}
                                                    className="input-field"
                                                    style={{ width: "20rem" }}
                                                />
                                            </div>
                                            <ErrorMessage
                                                name="email"
                                                component="div"
                                                className="text-danger mt-1 fs_12"
                                            />
                                        </div>

                                        <div className="text-start gap-3 mb-2 text-nowrap mx-4">
                                            <label htmlFor="user_type" className="py-1">
                                                User Type
                                            </label>
                                            <div className="d-flex position-relative">
                                                <input
                                                    value={values.user_type}
                                                    className="input-field"
                                                    style={{ width: "20rem" }}
                                                />
                                            </div>
                                            <ErrorMessage
                                                name="user_type"
                                                component="div"
                                                className="text-danger mt-1 fs_12"
                                            />
                                        </div>

                                        <div className="text-start gap-3 mb-2 text-nowrap mx-4">
                                            <label htmlFor="sign_in" className="py-1">
                                                Sign in method
                                            </label>
                                            <div className="d-flex position-relative">
                                                <input
                                                    value={values.sign_in}
                                                    className="input-field"
                                                    style={{ width: "20rem" }}
                                                />
                                            </div>
                                            <ErrorMessage
                                                name="sign_in"
                                                component="div"
                                                className="text-danger mt-1 fs_12"
                                            />
                                        </div>

                                    </div>
                                </Form>
                            )}
                        </Formik>
                    </div>
                }

                {/* Active Sessions */}
                <div className="px-4 pb-4 text-start">
                    <div className="d-flex align-items-center gap-3 mb-3 mt-4">
                        <h5 className="fs_20 fw_400 fn_Roboto mb-0">Active Sessions</h5>
                        {otherSessionsCount > 0 && (
                            <button
                                className="btn btn-sm btn-outline-danger d-flex align-items-center gap-1"
                                onClick={handleTerminateOthers}
                                disabled={terminatingOthers}
                            >
                                {terminatingOthers
                                    ? <Spinner animation="border" size="sm" />
                                    : <i className="bi bi-shield-x" />
                                }
                                Sign Out Other Sessions ({otherSessionsCount})
                            </button>
                        )}
                    </div>
                    <div className="card" style={{ overflow: 'hidden' }}>
                        {loadingSessions ? (
                            <div className="d-flex justify-content-center p-4">
                                <Spinner animation="border" variant="primary" />
                            </div>
                        ) : (
                            <div className="table-responsive">
                                <table className="table table-hover mb-0">
                                    <thead className="table-light">
                                        <tr>
                                            <th>Device</th>
                                            <th>IP Address</th>
                                            <th className="text-nowrap">Last Active</th>
                                            <th className="text-nowrap">Started</th>
                                            <th></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {sessions.length > 0 ? sessions.map((s) => (
                                            <tr key={s.id}>
                                                <td>
                                                    <div className="d-flex align-items-center gap-2">
                                                        <span>{s.device_summary || 'Unknown Device'}</span>
                                                        {s.is_current && (
                                                            <span className="badge bg-success ms-1">Current</span>
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="text-muted">{s.ip_address || '—'}</td>
                                                <td className="text-nowrap text-muted">
                                                    {moment(s.last_active_at).fromNow()}
                                                </td>
                                                <td className="text-nowrap text-muted">
                                                    {moment(s.createdAt).format('MMM D, YYYY h:mm A')}
                                                </td>
                                                <td>
                                                    {!s.is_current && (
                                                        <button
                                                            className="btn btn-sm btn-outline-danger"
                                                            onClick={() => handleTerminate(s.id)}
                                                            disabled={terminatingId === s.id}
                                                        >
                                                            {terminatingId === s.id
                                                                ? <Spinner animation="border" size="sm" />
                                                                : 'Sign Out'
                                                            }
                                                        </button>
                                                    )}
                                                </td>
                                            </tr>
                                        )) : (
                                            <tr>
                                                <td colSpan="5" className="text-center text-muted py-4">
                                                    No active sessions
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>

            </div>
        </div>
    );
};
export default ProfileScreen;